import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline'
import Skeleton from '@mui/material/Skeleton'
import DetailsRow from 'module/general/DetailsRow'
import EditBankDetailsModal from 'module/general/EditBankDetailsModal'
import EditBasicDetailsModal from 'module/general/EditBasicDetailsModal'
import default_company_photo from 'assets/images/default_company_photo.png'

import {
  firstTimeGenerateLink,
  getCompanyDetails,
  toggleJobBoardActivation,
} from 'services/api/company-details'

import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Icon, useMediaQuery, useTheme } from '@mui/material'
import AdxSwitch from 'components/reusable/switch/AdxSwitch'
import JobBoardActivationInfoModal from './components/JobBoardActivationInfoModal'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'
import AdxButton from 'components/custom/AdxButton'

const General = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [showModalEditBasicDetails, setShowModalEditBasicDetails] = useState(false)
  const [showModalBankDetails, setShowModalBankDetails] = useState(false)

  // snackbar logic
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)

  const { data: companyDetails, isLoading: loading } = useQuery('companyDetails', getCompanyDetails)
  const JOB_BOARD_IS_ACTIVE = 1
  const JOB_BOARD_IS_INACTIVE = 0

  const handleOpenModalEditBasicDetails = () => {
    setShowModalEditBasicDetails(true)
  }
  const handleCloseModalEditBasicDetails = () => {
    setShowModalEditBasicDetails(false)
  }
  const handleOpenModalBankDetails = () => {
    setShowModalBankDetails(true)
  }
  const handleCloseModalBankDetails = () => {
    setShowModalBankDetails(false)
  }

  const handleCopy = () => {
    const textToCopy = document.getElementById('text-to-copy').innerText
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        console.log('Text copied to clipboard')
        setSuccessSnackbarOpen(true)
      })
      .catch((error) => {
        console.error('Error copying text: ', error)
        setErrorSnackbarOpen(true)
      })
  }

  const queryClient = useQueryClient()

  const { mutate: generateLinkMutate } = useMutation(firstTimeGenerateLink, {
    onSuccess: (res) => {
      console.log(res)
      queryClient.invalidateQueries('companyDetails')
    },
    onError: (error) => {
      console.log(error)
    },
  })

  const { mutate: toggleJobBoardActivationMutate } = useMutation(toggleJobBoardActivation, {
    onSuccess: (res) => {
      console.log(res)
      queryClient.invalidateQueries('companyDetails')
    },
    onError: (error) => {
      console.log(error)
    },
  })

  const handleSwitchChange = () => {
    toggleJobBoardActivationMutate({
      activation:
        companyDetails?.data?.job_board === JOB_BOARD_IS_ACTIVE
          ? JOB_BOARD_IS_INACTIVE
          : JOB_BOARD_IS_ACTIVE,
    })
  }

  const company_uuid = companyDetails?.data?.tenant_identifier
  const company_name = companyDetails?.data?.organization_name

  const handleDescription = (value) => {
    if (!loading) {
      if (value) {
        return value
      } else {
        return '-'
      }
    } else {
      return <Skeleton />
    }
  }

  let basicCompanyDetails = [
    {
      title: 'Company name',
      description: handleDescription(companyDetails?.data?.organization_name),
    },
    {
      title: 'Company type',
      description: handleDescription(companyDetails?.data?.company_type_name),
    },
    {
      title: 'Registration number',
      description: handleDescription(companyDetails?.data?.ssm_number),
    },
    {
      title: 'Primary phone number',
      description: !loading ? (
        !companyDetails?.data?.company_phone_code || !companyDetails?.data?.company_phone_no ? (
          '-'
        ) : (
          `(+${companyDetails?.data?.company_phone_code})-${companyDetails?.data?.company_phone_no}`
        )
      ) : (
        <Skeleton />
      ),
    },
    {
      title: 'Primary address',
      description: !loading ? (
        !companyDetails?.data?.address &&
          !companyDetails?.data?.postcode &&
          !companyDetails?.data?.state &&
          !companyDetails?.data?.country ? (
          '-'
        ) : (
          `${companyDetails?.data?.address}, ${companyDetails?.data?.postcode}, ${companyDetails?.data?.state}, ${companyDetails?.data?.country}`
        )
      ) : (
        <Skeleton />
      ),
    },
  ]

  let bankCompanyDetails = [
    {
      title: 'Bank Name',
      description: handleDescription(companyDetails?.data?.bank_name),
    },
    {
      title: 'Maybank Corporate ID',
      description: handleDescription(companyDetails?.data?.company_bank_id),
    },
    {
      title: 'E-number',
      description: handleDescription(companyDetails?.data?.ea_number),
    },
    {
      title: 'Bank account number',
      description: handleDescription(companyDetails?.data?.bank_acc_number),
    },
    {
      title: 'EPF account number',
      description: handleDescription(companyDetails?.data?.epf_acc_number),
    },
    {
      title: 'SOCSO account number',
      description: handleDescription(companyDetails?.data?.socso_acc_number),
    },
    {
      title: 'SKBBK contribution status',
      description:
        companyDetails?.data?.skbbk_enabled === 1
          ? 'Yes'
          : companyDetails?.data?.skbbk_enabled === 0
            ? 'No'
            : '-',
    },
    {
      title: 'LHDN employer number',
      description: handleDescription(companyDetails?.data?.lhdn_acc_number),
    },
    {
      title: 'Zakat number',
      description: handleDescription(companyDetails?.data?.zakat_acc_number),
    },
    {
      title: 'HRDF contribution status',
      description:
        companyDetails?.data?.hrdf_status === 1
          ? 'Yes'
          : companyDetails?.data?.hrdf_status === 0
            ? 'No'
            : '-',
    },
    {
      title: 'HRDF contribution rate',
      description:
        companyDetails?.data?.hrdf_status === 0
          ? '-'
          : companyDetails?.data?.hrdf_rate === 1
            ? '1%'
            : companyDetails?.data?.hrdf_rate === 0
              ? '0.5%'
              : '-',
    },
    {
      title: 'HRDF account number',
      description: handleDescription(companyDetails?.data?.hrdf_acc_number),
    },
  ]
  return (
    <>
      <Box display="flex" width="100%" flexDirection="column" gap={2}>
        <Box px={{ xs: 2, lg: 0 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            Basic company details
          </Typography>
          <Box
            bgcolor="white.main"
            pt={2}
            sx={{ border: 1, borderRadius: '4.5px', borderColor: 'gray-line.main' }}
          >
            <Box px={2}>
              <Grid container justifyContent="center">
                <Box mb={{ xs: 4, lg: 2 }}>
                  {!loading ? (
                    <Box
                      as="img"
                      src={companyDetails?.data?.organization_logo || default_company_photo}
                      borderRadius="50%"
                      width={160}
                      height={160}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Skeleton variant="circular" width={160} height={160} />
                  )}
                </Box>
              </Grid>
              {basicCompanyDetails.map((item) => (
                <DetailsRow item={item} key={item.title} />
              ))}
            </Box>
            <Divider sx={{ marginTop: 2 }} />
            <Box p={0.5}>
              <AdxButton
                variant={'ghost'}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  gap: 1,
                }}
                onClick={() => handleOpenModalEditBasicDetails()}
              >
                <ModeEditOutlineIcon color="alternate" />
                <Typography fontWeight={500} color="alternate.main">
                  Edit basic details
                </Typography>
              </AdxButton>
            </Box>
          </Box>
        </Box>
        <Box px={{ xs: 2, lg: 0 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            Bank and statutory body details
          </Typography>
          <Box
            bgcolor="white.main"
            pt={2}
            sx={{ border: 1, borderRadius: '4.5px', borderColor: 'gray-line.main' }}
          >
            <Box px={2}>
              {bankCompanyDetails?.map((item) => (
                <DetailsRow item={item} key={item.title} />
              ))}
            </Box>
            <Divider sx={{ marginTop: 2 }} />
            <Box p={0.5}>
              <AdxButton
                variant={'ghost'}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  gap: 1,
                }}
                onClick={() => handleOpenModalBankDetails()}
              >
                <ModeEditOutlineIcon color="alternate" />
                <Typography fontWeight={500} color="alternate.main">
                  Edit bank and statutory body details
                </Typography>
              </AdxButton>
            </Box>
          </Box>
        </Box>
        <Box px={{ xs: 2, lg: 0 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              Job board settings
            </Typography>
            <JobBoardActivationInfoModal />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: 1,
              borderRadius: '4.5px',
              borderColor: 'gray-line.main',
              backgroundColor: 'white.main',
              width: '100%',
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderBottomColor: 'gray-line.main',
                padding: '20px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'default-background.main',
                    borderRadius: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '10px',
                  }}
                >
                  <Icon sx={{ color: 'alternate.main' }}>add_link</Icon>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography>Job board URL</Typography>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Generate a custom job board URL to list openings and attract potential
                    candidates.
                  </Typography>
                  <Box>
                    {companyDetails?.data?.first_time_job_board_activation === 1 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: isMobile ? '4px' : '13.5px 16px',
                          backgroundColor: 'gray-background.main',
                          border: 1,
                          borderColor: 'gray-line.main',
                          borderRadius: '4.8px',
                          marginTop: '16px',
                          width: '100%',
                        }}
                      >
                        <Typography id="text-to-copy" sx={{ display: isMobile && 'none' }}>
                          {`${window.location.origin}/job-board/${company_name}/${company_uuid}`}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'alternate.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                          onClick={handleCopy}
                        >
                          Copy link
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {isMobile && (
                    <>
                      {companyDetails?.data?.first_time_job_board_activation === 0 && (
                        <Box sx={{ marginTop: '8px' }}>
                          <AdxButton
                            variant={'contained'}
                            onClick={() => generateLinkMutate()}
                            sx={{ whiteSpace: 'nowrap', boxShadow: 'none', padding: '12px 10px' }}
                          >
                            <Icon sx={{ color: 'white.main', paddingRight: '4px' }}>add</Icon>
                            Generate link
                          </AdxButton>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
              {!isMobile && (
                <>
                  {companyDetails?.data?.first_time_job_board_activation === 0 && (
                    <Box>
                      <AdxButton
                        variant={'contained'}
                        onClick={() => generateLinkMutate()}
                        sx={{ whiteSpace: 'nowrap', boxShadow: 'none', padding: '12px 10px' }}
                      >
                        <Icon sx={{ color: 'white.main', paddingRight: '4px' }}>add</Icon>Generate
                        link
                      </AdxButton>
                    </Box>
                  )}
                </>
              )}
            </Box>
            <Box
              sx={{
                padding: '20px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'default-background.main',
                    borderRadius: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '10px',
                  }}
                >
                  <Icon sx={{ color: 'alternate.main' }}>check_circle</Icon>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography>Job board activation</Typography>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Activate your job board to display job postings and connect with potential
                    hires.
                  </Typography>
                  {isMobile && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'start',
                        alignItems: 'center',
                      }}
                    >
                      <Typography>
                        {companyDetails?.data?.job_board === 1 ? ' Active' : 'Inactive'}
                      </Typography>
                      <AdxSwitch
                        state={companyDetails?.data?.job_board === 1 ? true : false}
                        onChange={() => handleSwitchChange()}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography>
                    {companyDetails?.data?.job_board === 1 ? ' Active' : 'Inactive'}
                  </Typography>
                  <AdxSwitch
                    state={companyDetails?.data?.job_board === 1 ? true : false}
                    onChange={() => handleSwitchChange()}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <EditBasicDetailsModal
        showModalEditBasicDetails={showModalEditBasicDetails}
        handleCloseModalEditBasicDetails={handleCloseModalEditBasicDetails}
        companyDetails={companyDetails}
      />
      <EditBankDetailsModal
        showModalBankDetails={showModalBankDetails}
        handleCloseModalBankDetails={handleCloseModalBankDetails}
        companyDetails={companyDetails}
      />
      <SnackbarRecruitment
        autoHideDuration={3000}
        status="error"
        TransitionComponent="slide"
        message="An error has occured. Please try again"
        open={errorSnackbarOpen}
        handleClose={() => setErrorSnackbarOpen(false)}
      />
      <SnackbarRecruitment
        autoHideDuration={3000}
        status="success"
        TransitionComponent="slide"
        message="Link copied to clipboard"
        open={successSnackbarOpen}
        handleClose={() => setSuccessSnackbarOpen(false)}
      />
    </>
  )
}

export default General
